import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Gavel } from "lucide-react";

export default function StartAuctionModal({ productId, productTitle, onClose }) {
  const navigate = useNavigate();
  const [startingPrice, setStartingPrice] = useState("");
  const [reservePrice, setReservePrice] = useState("");
  const [duration, setDuration] = useState(7);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    const sp = parseFloat(startingPrice);
    if (isNaN(sp) || sp <= 0) { toast.error("Prix de départ invalide"); return; }
    const rp = reservePrice ? parseFloat(reservePrice) : undefined;
    if (rp !== undefined && (isNaN(rp) || rp < sp)) { toast.error("Le prix de réserve doit être ≥ prix de départ"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auctions", {
        product_id: productId,
        starting_price: sp,
        reserve_price: rp,
        duration_days: duration,
      });
      toast.success("Enchère lancée ⚡");
      navigate(`/encheres/${data.auction_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <>
      <div className="sheet-backdrop" onClick={onClose} />
      <div className="fixed left-3 right-3 bottom-3 lg:left-1/2 lg:bottom-auto lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:w-[480px] bg-white border-2 border-black z-[80] slide-up max-h-[90vh] overflow-y-auto" data-testid="start-auction-modal">
        <div className="p-4 hairline-b flex items-center justify-between bg-[color:var(--color-mint)]">
          <div className="flex items-center gap-2">
            <Gavel size={20} strokeWidth={2.5}/>
            <h3 className="font-black text-lg">Mettre aux enchères</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center" data-testid="close-auction-modal"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-[color:var(--color-text-muted)]"><strong>{productTitle}</strong></p>

          <div>
            <label className="label font-bold mb-2 block">Prix de départ (€) *</label>
            <input type="number" step="1" min="1" className="field" value={startingPrice} onChange={e=>setStartingPrice(e.target.value)} placeholder="5" data-testid="auction-starting-price"/>
          </div>

          <div>
            <label className="label font-bold mb-2 block">Prix de réserve secret (€) — optionnel</label>
            <input type="number" step="1" min="1" className="field" value={reservePrice} onChange={e=>setReservePrice(e.target.value)} placeholder="Ex: 50" data-testid="auction-reserve-price"/>
            <p className="text-xs mt-1 text-[color:var(--color-text-muted)]">Si la plus haute offre n'atteint pas ce prix, l'enchère se termine sans vente. Caché aux acheteurs.</p>
          </div>

          <div>
            <label className="label font-bold mb-2 block">Durée</label>
            <div className="grid grid-cols-4 gap-2">
              {[1, 3, 7, 14].map(d => (
                <button key={d} type="button" onClick={()=>setDuration(d)}
                  className={`btn btn-sm ${duration === d ? 'btn-primary' : 'btn-ghost'}`}
                  data-testid={`duration-${d}`}>
                  {d}j
                </button>
              ))}
            </div>
          </div>

          <div className="p-3 bg-[color:var(--color-surface)] text-xs">
            <p className="font-bold mb-1">📋 Règles enchères Fripri</p>
            <ul className="space-y-0.5 text-[color:var(--color-text-muted)]">
              <li>• Anti-sniping : mises dans les 2 dernières min → +2 min</li>
              <li>• Gagnant a 24h pour payer, sinon offre au 2ème</li>
              <li>• Tu reçois 100% du prix final — les frais de protection sont payés par l'acheteur</li>
            </ul>
          </div>

          <button onClick={submit} disabled={loading} className="btn btn-primary w-full" data-testid="confirm-auction-btn">
            {loading ? "Lancement…" : "Lancer l'enchère ⚡"}
          </button>
        </div>
      </div>
    </>
  );
}
